import React, {useEffect, useRef, useState} from 'react'
import {makeStyles} from '@mui/styles'
import RXRIcon from './RXRIcon'
import {Colors, Spacing, Typography} from '../assets/styles'
import {formatElapsedTime} from '../Utils/dateTimeUtil'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    backgroundColor: Colors.blackOverlay80,
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    padding: Spacing.spaceMedium,
    gap: Spacing.spaceMedium,
  },
  title: {
    gridColumn: 2,
    textAlign: 'center',
    color: Colors.rxrWhiteColor,
    fontSize: Typography.fontSizeMediumLarge,
  },
  closeButton: {
    gridColumn: 3,
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceExtraSmall,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: Colors.rxrWhiteColor,
    fontSize: Typography.fontSizeMediumLarge,
    textDecoration: 'underline',
  },
  stage: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `0 ${Spacing.spaceMedium}px ${Spacing.spaceLarge}px`,
  },
  videoWrap: {
    position: 'relative',
    display: 'inline-flex',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  video: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
    cursor: 'pointer',
  },
  controls: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: Spacing.spaceSmall,
    padding: Spacing.spaceSmall,
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceSmall,
  },
  playButton: {
    flexShrink: 0,
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: Colors.rxrMediumGreyColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playGlyph: {
    marginLeft: 3,
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    borderLeft: `10px solid ${Colors.rxrWhiteColor}`,
  },
  pauseGlyph: {
    width: 8,
    height: 11,
    borderLeft: `3px solid ${Colors.rxrWhiteColor}`,
    borderRight: `3px solid ${Colors.rxrWhiteColor}`,
  },
  timePill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `${Spacing.spaceExtraExtraSmall}px ${Spacing.spaceSmall}px`,
    borderRadius: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    fontSize: Typography.fontSizeMedium,
  },
  timeCurrent: {
    color: Colors.rxrWhiteColor,
    fontWeight: Typography.mediumFontWeight,
  },
  timeTotal: {
    color: Colors.rxrMediumLightGreyColor,
  },
  scrubber: {
    width: '100%',
    WebkitAppearance: 'none',
    appearance: 'none',
    height: 4,
    borderRadius: 2,
    outline: 'none',
    cursor: 'pointer',
    background: Colors.rxrMediumLightGreyColor,
    '&::-webkit-slider-thumb': {
      WebkitAppearance: 'none',
      appearance: 'none',
      width: 14,
      height: 14,
      borderRadius: '50%',
      backgroundColor: Colors.rxrPrimaryColor,
      cursor: 'pointer',
    },
    '&::-moz-range-thumb': {
      width: 14,
      height: 14,
      border: 'none',
      borderRadius: '50%',
      backgroundColor: Colors.rxrPrimaryColor,
      cursor: 'pointer',
    },
    '&::-moz-range-progress': {
      height: 4,
      borderRadius: 2,
      backgroundColor: Colors.rxrPrimaryColor,
    },
    '&::-moz-range-track': {
      height: 4,
      borderRadius: 2,
      backgroundColor: Colors.rxrMediumLightGreyColor,
    },
  },
}))

/**
 *
 * @param {object} props
 * @param {{url: string, durationSeconds: number}} props.recording
 * @param {() => void} props.onClose
 * @param {string} [props.title]
 * @param {string} [props.preload]
 * @param {() => void} [props.onReady]
 * @param {() => void} [props.onError]
 */
function VideoPlayerOverlay({recording, onClose, title, preload, onReady, onError}) {
  const classes = useStyles()
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(recording.durationSeconds || 0)

  useEffect(() => {
    const handleKey = event => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) {
      return
    }
    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const handleSeek = event => {
    const nextTime = Number(event.target.value)
    if (videoRef.current) {
      videoRef.current.currentTime = nextTime
    }
    setCurrentTime(nextTime)
  }

  const handleStageClick = event => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  const playedPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className={classes.overlay}>
      <div className={classes.topBar}>
        {title && <span className={classes.title}>{title}</span>}
        <button type="button" className={classes.closeButton} onClick={onClose}>
          <RXRIcon icon={RXRIcon.CLOSE} size={16} color={Colors.rxrWhiteColor} />
          Close
        </button>
      </div>
      <div className={classes.stage} onClick={handleStageClick}>
        <div className={classes.videoWrap}>
          <video
            ref={videoRef}
            className={classes.video}
            src={recording.url}
            playsInline
            preload={preload}
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onLoadedMetadata={event => {
              if (Number.isFinite(event.target.duration)) {
                setDuration(event.target.duration)
              }
            }}
            onLoadedData={() => onReady && onReady()}
            onCanPlay={() => onReady && onReady()}
            onError={() => onError && onError()}
            onTimeUpdate={event => setCurrentTime(event.target.currentTime)}
          />
          <div className={classes.controls}>
            <input
              type="range"
              className={classes.scrubber}
              min={0}
              max={duration || 0}
              step="any"
              value={Math.min(currentTime, duration || 0)}
              onChange={handleSeek}
              style={{
                background: `linear-gradient(to right, ${Colors.rxrPrimaryColor} ${playedPercent}%, ${Colors.rxrMediumLightGreyColor} ${playedPercent}%)`,
              }}
            />
            <div className={classes.controlRow}>
              <button type="button" className={classes.playButton} onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
                <span className={isPlaying ? classes.pauseGlyph : classes.playGlyph} />
              </button>
              <div className={classes.timePill}>
                <span className={classes.timeCurrent}>{formatElapsedTime(currentTime)}</span>
                <span className={classes.timeTotal}>{` / ${formatElapsedTime(duration)}`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoPlayerOverlay
